import "@/index.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AdminProvider } from "./context/AdminContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { HomePage } from "./pages/HomePage";
import { ServicesPage } from "./pages/ServicesPage";
import { BoatsPage } from "./pages/BoatsPage";
import { GalleryPage } from "./pages/GalleryPage";
import { EventsPage } from "./pages/EventsPage";
import { ContactPage } from "./pages/ContactPage";
import { QuotePage } from "./pages/QuotePage";
import { AdminPage } from "./pages/AdminPage";
import { PartnersPage } from "./pages/PartnersPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { Toaster } from "./components/ui/sonner";
import CanonicalTag from "./components/CanonicalTag";

/**
 * Redirect that preserves ?lang=XX query param.
 * e.g. /trasporti/?lang=de  →  /services?lang=de
 */
const LangRedirect = ({ to }) => {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang');
  return <Navigate to={lang ? `${to}?lang=${lang}` : to} replace />;
};

/**
 * Handles old WordPress multilingual paths like /de/occasioni/ or /fr/trasporti/
 * Maps known WordPress slugs to new React paths, preserving the language.
 */
const WpLangPathRedirect = ({ lang }) => {
  const [searchParams] = useSearchParams();
  const slug = window.location.pathname
    .replace(/^\/(de|fr|en|it)\//, '')  // strip language prefix
    .replace(/\/$/, '');                 // strip trailing slash

  const slugMap = {
    occasioni:     '/boats',
    esposizione:   '/boats',
    'barche-usate': '/boats',
    deposito:      '/services',
    trasporti:     '/services',
    servizi:       '/services',
    'eventi-e-news': '/events',
    eventi:        '/events',
    news:          '/events',
    contatto:      '/contact',
    'chi-siamo':   '/',
    partner:       '/partners',
    collaborazioni: '/partners',
    galleria:      '/gallery',
  };

  const target = slugMap[slug] || '/';
  const extraLang = searchParams.get('lang') || lang;
  const finalTarget = (extraLang && extraLang !== 'it') ? `${target}?lang=${extraLang}` : target;
  return <Navigate to={finalTarget} replace />;
};

/**
 * Redirect old WordPress ?attachment_id= URLs to homepage.
 * e.g. /?attachment_id=3215&lang=de  →  /?lang=de
 */
const AttachmentRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = React.useRef(null);
  // Use window.location directly for immediate redirect before render
  React.useEffect(() => {
    if (searchParams.get('attachment_id')) {
      const lang = searchParams.get('lang');
      window.location.replace(lang ? `/?lang=${lang}` : '/');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

function App() {
  return (
    <LanguageProvider>
      <AdminProvider>
        <div className="App min-h-screen flex flex-col">
          <BrowserRouter>
            <Navbar />
            <CanonicalTag />
            <AttachmentRedirect />
            <main className="flex-grow">
              <Routes>
                {/* ── Main routes ── */}
                <Route path="/" element={<HomePage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/boats" element={<BoatsPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/quote" element={<QuotePage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/admin" element={<AdminPage />} />

                {/* ── WordPress Italian redirects (with ?lang= preservation) ── */}
                <Route path="/occasioni" element={<LangRedirect to="/boats" />} />
                <Route path="/occasioni/" element={<LangRedirect to="/boats" />} />
                <Route path="/esposizione" element={<LangRedirect to="/boats" />} />
                <Route path="/esposizione/" element={<LangRedirect to="/boats" />} />
                <Route path="/deposito" element={<LangRedirect to="/services" />} />
                <Route path="/deposito/" element={<LangRedirect to="/services" />} />
                <Route path="/trasporti" element={<LangRedirect to="/services" />} />
                <Route path="/trasporti/" element={<LangRedirect to="/services" />} />
                <Route path="/eventi-e-news" element={<LangRedirect to="/events" />} />
                <Route path="/eventi-e-news/" element={<LangRedirect to="/events" />} />
                <Route path="/contatto" element={<LangRedirect to="/contact" />} />
                <Route path="/contatto/" element={<LangRedirect to="/contact" />} />
                <Route path="/chi-siamo" element={<LangRedirect to="/" />} />
                <Route path="/chi-siamo/" element={<LangRedirect to="/" />} />
                <Route path="/privacy-policy" element={<LangRedirect to="/contact" />} />
                <Route path="/privacy-policy/" element={<LangRedirect to="/contact" />} />
                <Route path="/impressum" element={<LangRedirect to="/contact" />} />
                <Route path="/impressum/" element={<LangRedirect to="/contact" />} />
                <Route path="/feed" element={<Navigate to="/" replace />} />
                <Route path="/feed/" element={<Navigate to="/" replace />} />
                <Route path="/wp-admin" element={<Navigate to="/admin" replace />} />
                <Route path="/wp-admin/" element={<Navigate to="/admin" replace />} />
                <Route path="/wp-login.php" element={<Navigate to="/admin" replace />} />

                {/* ── WordPress multilingual path redirects (/de/, /fr/, /en/, /it/) ── */}
                <Route path="/de/*" element={<WpLangPathRedirect lang="de" />} />
                <Route path="/fr/*" element={<WpLangPathRedirect lang="fr" />} />
                <Route path="/en/*" element={<WpLangPathRedirect lang="en" />} />
                <Route path="/it/*" element={<WpLangPathRedirect lang="it" />} />

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            <Footer />
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AdminProvider>
    </LanguageProvider>
  );
}

export default App;
